import { memo, useEffect, useRef } from 'react';
import { CollectionPointWithI18nKey } from '../../../../types/collectionPoints';
import { config, getAssetUrl } from '../../../../utils/config';
import useDeepCompare from '../../../../utils/useDeepCompare';
import { ViewOption } from './ViewOptions';
import './style.scss';

const DEFAULT_INITIAL_CENTER_COORDINATES = { latitude: 53.5483197, longitude: 10.0006301 };

type ExtendedViewOption = ViewOption | 'detail';

type LocationsMapProps = {
  locations?: CollectionPointWithI18nKey[];
  onMarkerClick: (id: string) => void;
  viewOption: ExtendedViewOption;
};

const Map = ({ locations, onMarkerClick, viewOption }: LocationsMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map>();
  const previousMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>();
  const currentLocations = useDeepCompare(locations);
  const { country, appId, environment } = window.ayCheckout?.appQualifier ?? {};
  useEffect(() => {
    const { latitude, longitude } =
      config.availableCountries.shipping[0].centerCoordinates ?? DEFAULT_INITIAL_CENTER_COORDINATES;
    try {
      mapRef.current = new google.maps.Map(mapContainerRef.current!, {
        disableDefaultUI: true,
        zoomControl: true,
        mapId: `${environment}-${country}-${appId}`,
        zoom: 5,
        keyboardShortcuts: false,
        center: { lat: latitude, lng: longitude }
      });
    } catch (e: any) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    previousMarkersRef.current?.forEach(marker => (marker.map = null));
    if (!currentLocations?.length || viewOption === 'list') {
      return;
    }
    previousMarkersRef.current = currentLocations.map(location => {
      const { latitude, longitude } = location.displayData.coordinates;
      const { collectionPointId } = location.specification;
      const pinElement = document.createElement('img');
      const mapPinIcon = currentLocations.length === 1 ? `${location.i18nKey}Selected` : location.i18nKey;
      pinElement.setAttribute('src', getAssetUrl(`collectionPoints/mapPins/${mapPinIcon}.svg`));
      pinElement.setAttribute('width', '46px');

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: latitude, lng: longitude },
        map: mapRef.current!,
        content: pinElement
      });
      marker.addListener('click', () => onMarkerClick?.(collectionPointId));
      return marker;
    });
    const bounds = new google.maps.LatLngBounds();
    currentLocations.slice(0, 7).forEach(location => {
      const { latitude, longitude } = location.displayData.coordinates;
      bounds.extend({ lat: latitude, lng: longitude });
    });
    if (currentLocations.length === 1) {
      const { latitude, longitude } = currentLocations[0].displayData.coordinates;
      mapRef.current!.setCenter({
        lat: latitude,
        lng: longitude
      });
      mapRef.current!.setZoom(18);
    } else {
      mapRef.current!.fitBounds(bounds);
    }
  }, [currentLocations, viewOption]);

  return <div className="map" ref={mapContainerRef} />;
};

export default memo(Map);
